import { ReservationForm } from '@/types';

import { Input } from '@/components/ui/input';
import {
    availableEnd,
    formatTo12Hour,
    HOURS_24,
} from '@/lib/reservation.utils';
import { cn } from '@/lib/utils';
import { format, startOfDay } from 'date-fns';
import { CalendarIcon, Users } from 'lucide-react';
import { FaWhatsapp } from 'react-icons/fa';
import { Button } from './ui/button';
import { Calendar } from './ui/calendar';
import { InputGroup, InputGroupAddon, InputGroupInput } from './ui/input-group';
import { Label } from './ui/label';
import { Popover, PopoverContent, PopoverTrigger } from './ui/popover';
import {
    Select,
    SelectContent,
    SelectItem,
    SelectTrigger,
    SelectValue,
} from './ui/select';

interface Props {
    data: ReservationForm;
    setData: <K extends keyof ReservationForm>(
        key: K,
        value: ReservationForm[K],
    ) => void;
    errors: Partial<Record<keyof ReservationForm, string>>;
    room: App.Data.RoomData | App.Data.TableData;
    reservedHours: string[];
    loadingHours: boolean;
}

export function ReservationFormFields({
    data,
    setData,
    errors,
    room,
    reservedHours,
    loadingHours,
}: Props) {
    const isToday = data.date === format(new Date(), 'yyyy-MM-dd');
    const availableStart = HOURS_24.filter((h) => {
        if (!isToday) return !reservedHours.includes(h);

        const currentHour = new Date().getHours();
        return (
            Number(h.split(':')[0]) > currentHour && !reservedHours.includes(h)
        );
    });

    return (
        <>
            {/* NAME */}
            <div className="space-y-2">
                <Label className="flex items-center gap-2">Name</Label>
                <Input
                    name="name"
                    placeholder="Your Name"
                    required
                    value={data.name}
                    onChange={(e) => setData('name', e.target.value)}
                />
            </div>
            {/* WHATSAPP */}
            <div className="space-y-2">
                <Label className="flex items-center gap-2">WhatsApp</Label>
                <InputGroup>
                    <InputGroupInput
                        placeholder="Phone Number"
                        value={data.phone}
                        onChange={(e) => setData('phone', e.target.value)}
                        type="tel"
                    />
                    <InputGroupAddon>
                        <FaWhatsapp className="h-4 w-4 text-green-600" />
                    </InputGroupAddon>
                    <InputGroupAddon>+20</InputGroupAddon>
                    <p className="text-sm text-red-600">{errors.phone}</p>
                </InputGroup>
            </div>
            {/* GUESTS */}
            <div className="space-y-2">
                <Label className="flex items-center gap-2">
                    <Users className="h-4 w-4" />
                    Number of Guests
                </Label>

                <div className="flex items-center gap-2">
                    {/* Minus */}
                    <Button
                        type="button"
                        variant="outline"
                        size="icon"
                        disabled={data.guests <= 1}
                        onClick={() =>
                            setData('guests', Math.max(1, data.guests - 1))
                        }
                    >
                        −
                    </Button>

                    {/* Value (same as shadcn GPU example) */}
                    <Input
                        type="number"
                        inputMode="numeric"
                        min={1}
                        max={room.capacity}
                        value={data.guests}
                        onChange={(e) => {
                            const value = Number(e.target.value);
                            if (!Number.isNaN(value)) {
                                setData(
                                    'guests',
                                    Math.min(room.capacity, Math.max(1, value)),
                                );
                            }
                        }}
                        className="w-16 text-center"
                    />

                    {/* Plus */}
                    <Button
                        type="button"
                        variant="outline"
                        size="icon"
                        disabled={data.guests >= room.capacity}
                        onClick={() =>
                            setData(
                                'guests',
                                Math.min(room.capacity, data.guests + 1),
                            )
                        }
                    >
                        +
                    </Button>
                </div>

                <p className="text-xs text-muted-foreground">
                    Maximum {room.capacity} guests
                </p>
            </div>
            {/* DATE PICKER */}
            <div className="flex flex-col space-y-2">
                <Label className="flex items-center gap-2">Date</Label>
                <Popover>
                    <PopoverTrigger asChild>
                        <Button
                            variant="outline"
                            className={cn(
                                'justify-start font-normal',
                                !data.date && 'text-muted-foreground',
                            )}
                            type="button"
                        >
                            <CalendarIcon className="mr-2 h-4 w-4" />
                            {data.date
                                ? format(
                                      new Date(`${data.date}T00:00:00`),
                                      'PPP',
                                  )
                                : 'Pick a date'}
                        </Button>
                    </PopoverTrigger>
                    <PopoverContent className="p-0">
                        <Calendar
                            mode="single"
                            selected={
                                data.date ? new Date(data.date) : undefined
                            }
                            onSelect={(date) =>
                                setData(
                                    'date',
                                    date ? format(date, 'yyyy-MM-dd') : '',
                                )
                            }
                            disabled={(date) => date < startOfDay(new Date())}
                        />
                    </PopoverContent>
                </Popover>
                <p className="text-sm text-red-600">{errors.date}</p>
            </div>
            {/* HOURS */}
            {data.date && (
                <div className="space-y-2">
                    {loadingHours ? (
                        <div className="flex justify-center py-3">
                            <div className="h-6 w-6 animate-spin rounded-full border-2 border-primary border-t-transparent" />
                        </div>
                    ) : (
                        <div className="grid grid-cols-2 gap-4">
                            {/* Start Hour */}
                            <div className="space-y-2">
                                <Label>Start</Label>
                                <div className="flex flex-col">
                                    <Select
                                        onValueChange={(value) =>
                                            setData('start_time', value)
                                        }
                                        value={data.start_time}
                                    >
                                        <SelectTrigger>
                                            <SelectValue placeholder="Start" />
                                        </SelectTrigger>
                                        <SelectContent>
                                            {availableStart.map((h) => (
                                                <SelectItem key={h} value={h}>
                                                    {formatTo12Hour(h)}
                                                </SelectItem>
                                            ))}
                                        </SelectContent>
                                    </Select>
                                </div>
                            </div>

                            {/* End Hour */}
                            <div className="space-y-2">
                                <Label>End</Label>
                                <div className="flex flex-col">
                                    <Select
                                        onValueChange={(value) =>
                                            setData('end_time', value)
                                        }
                                        value={data.end_time}
                                    >
                                        <SelectTrigger>
                                            <SelectValue placeholder="End" />
                                        </SelectTrigger>
                                        <SelectContent>
                                            {availableEnd(
                                                data,
                                                reservedHours,
                                            ).map((h) => (
                                                <SelectItem key={h} value={h}>
                                                    {formatTo12Hour(h)}
                                                    {data.start_time &&
                                                        h <=
                                                            data.start_time && (
                                                            <span className="ml-1 text-xs text-muted-foreground">
                                                                (Next day)
                                                            </span>
                                                        )}
                                                </SelectItem>
                                            ))}
                                        </SelectContent>
                                    </Select>
                                </div>
                            </div>
                        </div>
                    )}
                </div>
            )}
        </>
    );
}
